import { IonSpinner } from '@ionic/react'
import React, { useContext, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import Button from '../../../components/actions/Button'
import Stepper, { StepConfig } from '../../../components/stepper/Stepper'
import { ModalContext } from '../../../contexts/ModalContext'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../modules/trpc'
import ModalWrapper from '../../../wrappers/modalWrapper/ModalWrapper'
import AdditionalDetailsStep from './steps/AdditionalDetailsStep'
import ChooseContactCategoryStep from './steps/ChooseContactCategoryStep'

interface GeneralContactModalProps {}

export interface GeneralContactFormFields {
	category: string
	additionalDetails: string
}

const GeneralContactModal: React.FC<GeneralContactModalProps> = () => {
	const { mutateAsync: createGeneral } = trpc.contact.createGeneral.useMutation<string>()
	const { insertModal } = useContext(ModalContext)
	const { t: tc } = useTranslation('modals_generalContact')
	const [isSending, setIsSending] = useState<boolean>(false)
	const { t } = useTranslation('pages_contractConfirmation')
	const { t: tcm } = useTranslation('modals_moveOutContact')
	const [activeStep, setActiveStep] = useState(0)
	const object = useContext(ObjectContext)

	const form = useForm<GeneralContactFormFields>({
		mode: 'onBlur',
		defaultValues: {
			category: '',
		},
	})
	const handleNext = async () => {
		logEvent(`click-invoice-extension-next-step-${activeStep + 1}`)
		setActiveStep(prevActiveStep => prevActiveStep + 1)
	}

	const handlePrev = () => {
		logEvent(`click-invoice-extension-previous-step-${activeStep - 1}`)
		setActiveStep(prevActiveStep => prevActiveStep - 1)
	}

	const steps: StepConfig<GeneralContactFormFields>[] = [
		{
			label: tc('categoryStep.title'),
			key: 'MoveoutTypeStep',
			element: <ChooseContactCategoryStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disableNext: () => !form.watch('category'),
		},
		{
			label: tcm('additionalDetailsStep.title'),
			key: 'AdditionalDetailsStep',
			element: <AdditionalDetailsStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
		},
	]

	const handleSubmit = async () => {
		setIsSending(true)
		const { category, additionalDetails } = form.getValues()

		if (process.env.react_app_environment !== 'LOCAL') {
			await createGeneral({
				objectId: object.id,
				category: category,
				additionalDetails: additionalDetails,
			})
		}
		setIsSending(false)
		insertModal('contact-confirmation')
	}

	return (
		<ModalWrapper>
			<form
				id='get-our-contract'
				onSubmit={handleSubmit}
				style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
			>
				<Stepper
					steps={steps}
					activeStep={activeStep}
					onNext={handleNext}
					onPrev={handlePrev}
					form={form}
					lastStepAction={
						<Button disabled={isSending} onClick={handleSubmit}>
							{isSending ? <IonSpinner /> : t('valmis', { ns: 'common' })}
						</Button>
					}
				/>
			</form>
		</ModalWrapper>
	)
}

export default GeneralContactModal
